import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, Divider, Stack, Typography, useMediaQuery } from '@mui/material'
import SliderMui from '@mui/material/Slider'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import GraphPeriod from 'components/GraphPeriod'
import LineGraph, { GraphPeriodType } from 'components/LineGraph/LineGraph'
import { DataPoint } from 'components/LineGraph/types'
import { buildDataHistoricalSpotPrices } from 'components/LineGraph/utils'
import Location from 'components/Location'
import { myVaultPath, reviewPath } from 'components/Routes'
import {
  dashboardPath,
  referralsPath,
  tradePath
} from 'components/Routes/Routes'
import DefaultLayout from 'components/layout/DefaultLayout'
import LoadingWrapper from 'components/layout/LoadingWrapper'
import { TRADING_IS_PAUSED, DEFAULT_BUY_AMOUNT } from 'constants/index'
import { NumberValue } from 'd3'
import { useMutateCreateReviewOrder } from 'hooks/useTrading'
import { useHistoricalSpotPrices } from 'hooks/useInventory'
import useProducts from 'hooks/useProducts'
import useSpotPrice from 'hooks/useSpotPrice'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import theme from 'utils/theme'
import { formatDateToString, formattedPrice } from 'utils/util'
import {
  MAX_QUANTITY_MG,
  MIN_QUANTITY,
  decrementQuantity,
  incrementQuantity,
  stepFor
} from 'utils/quantitySlider'
import PauseTrading from '../PauseTrading'
import InputNumber from './InputNumber'
import useStyles from './styles'
import CountUpPrice from 'components/CountUpPrice'

export default function TradeProduct(): JSX.Element {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const { user, setToast } = useAuth()

  const isDesktopView = useMediaQuery(theme.breakpoints.up('md'))

  const { product, products, productId, productUnavailable } = useProducts()

  // A deep link to a product this customer is not offered (e.g. platinum with
  // the feature flag off) goes to the dashboard instead of a dead trade screen.
  useEffect(() => {
    if (productUnavailable) {
      navigate(dashboardPath(), { replace: true })
    }
  }, [navigate, productUnavailable])

  const [graphPeriod, setGraphPeriod] = useState<GraphPeriodType>('1w')
  const [tradingPaused, setTradingPaused] = useState<boolean>(false)

  const [headerHeight, setHeaderHeight] = useState<number>(0)
  const [productBullionHeight, setProductBullionHeight] = useState<number>(0)

  const unitType = product?.unitType ?? 'mg'

  const [weightSlider, setWeightSlider] = useState<number>(0)
  const [amount, setAmount] = useState<string>('0')
  // Which control the customer last drove. Typing a DOLLAR figure orders amount-first
  // so that figure is honoured exactly; the slider and grams box still order by
  // quantity, because dragging to 0.5g should buy 0.5g and not whatever amount-first
  // solves back to.
  const [entryMode, setEntryMode] = useState<'amount' | 'quantity'>('quantity')

  const [error, setError] = useState<
    { message: string; error: boolean } | undefined
  >()
  const [step, setStep] = useState<number>(MIN_QUANTITY)

  // weightSlider holds the quantity in the product's unit_type (e.g. mg
  // for gold/silver). All price math is mg-native: amount = quantity * pricePerMg.
  const calculateAmount = useCallback(
    (quantity: number, askPricePerMg: string | number) => {
      return (quantity * Number(askPricePerMg)).toFixed(2).toString()
    },
    []
  )

  const setWeight = useCallback(
    (quantity: number, askPricePerMg: string | number) => {
      const clamped = Math.max(
        Math.min(Math.round(quantity), MAX_QUANTITY_MG),
        MIN_QUANTITY
      )
      setWeightSlider(clamped)
      setAmount(calculateAmount(clamped, askPricePerMg))
    },
    [calculateAmount]
  )

  const createReviewOrderMutation = useMutateCreateReviewOrder()
  const creatingReviewOrderForBuy = createReviewOrderMutation.isLoading

  const onSubmit = useCallback(() => {
    if (productId && !error?.error) {
      createReviewOrderMutation.mutate(
        // A typed dollar figure is ordered AMOUNT-FIRST: the backend solves the
        // after-fee weight so the total equals what was entered, to the cent. Ordering
        // by quantity meant an arbitrary amount had to be snapped to the nearest whole
        // milligram (~24c of gold) and the box rewritten — the customer typed $88.55,
        // finished, and watched it become $88.51 (Greg, 8-11 Aug).
        entryMode === 'amount' && Number(amount) > 0
          ? {
              productId,
              orderType: 'buy' as const,
              totalAmount: Number(amount).toFixed(2),
              paymentMethod: 'wallet' as const
            }
          : {
              productId,
              orderType: 'buy' as const,
              quantityMg: weightSlider
            },
        {
          onSuccess: data => {
            if (data.id) {
              navigate(
                reviewPath({
                  queryParams: { type: 'buy', productId: productId },
                  routeParams: { reviewOrderId: data.id }
                })
              )
            }
          },
          onError: (err: unknown) => {
            const error = err as { message?: string; errorStatus?: string }
            if (error?.errorStatus === TRADING_IS_PAUSED) {
              setTradingPaused(true)
            }
            setToast({
              open: true,
              message: error?.message ?? 'An error occurred',
              type: 'error',
              duration: 3000
            })
          }
        }
      )
    }
  }, [
    // amount and entryMode decide WHAT is ordered, so a stale closure here would send
    // a figure the customer has since changed. CI lints with --max-warnings=0 and
    // caught their absence.
    amount,
    createReviewOrderMutation,
    entryMode,
    error,
    navigate,
    productId,
    setToast,
    weightSlider
  ])

  const {
    data: historicalSpotPriceData,
    isLoading: historicalSpotPriceLoading
  } = useHistoricalSpotPrices(productId ?? 0, graphPeriod)

  const [, { data: latestSpotPriceData, loading: gettingLatestSpotPrice }] =
    useSpotPrice({
      callInterval: 90000, // 1.5 minutes,
      productId
    })

  // Seed the opening amount once the price is known — driven by the DATA, not by
  // the fetch callback.
  //
  // This used to live in useSpotPrice's onCompleted, which only fires when a
  // request actually resolves. react-query holds the price for staleTime (30s),
  // so arriving here by client-side navigation (e.g. clicking the GOLDIE logo)
  // hit the cache, the callback never ran, and the screen sat at "BUY: $0.00".
  // A hard refresh forced a real fetch and looked fine — which is exactly the
  // difference Greg saw.
  //
  // Tracking WHICH product was seeded, rather than a plain "done" flag, because
  // switching product navigates within /trade/:productId — the same route — so
  // the component is never remounted. A boolean would leave silver priced with
  // gold's dollar amount. It also stops the 90-second price refresh from
  // clobbering an amount the customer has already chosen: under the old
  // callback every refetch reset the slider back to the default.
  const [seededForProduct, setSeededForProduct] = useState<number | null>(null)

  useEffect(() => {
    if (seededForProduct === productId) return
    const askPricePerMg = Number(latestSpotPriceData?.askPricePerMg)
    if (!(askPricePerMg > 0)) return

    // Sized so the BUTTON reads the default amount — that is the number the
    // customer sees. Fees are disclosed separately at checkout, computed by the
    // backend. Opening at a fixed amount rather than at what the wallet can
    // afford: the old logic collapsed to the MINIMUM for anyone with a low or
    // NEGATIVE balance (Cam's was -$11.70), and checkout collects a top-up
    // anyway, so a customer with no cash can still buy.
    const quantityForDefaultAmount = Math.round(
      Number(DEFAULT_BUY_AMOUNT) / askPricePerMg
    )
    const initWeight = Math.max(
      MIN_QUANTITY,
      Math.min(quantityForDefaultAmount, MAX_QUANTITY_MG)
    )

    setWeight(initWeight, askPricePerMg)
    setStep(stepFor(initWeight))
    setSeededForProduct(productId)
  }, [latestSpotPriceData, seededForProduct, productId, setWeight])

  const loading = useMemo(() => {
    return (
      creatingReviewOrderForBuy ||
      gettingLatestSpotPrice ||
      historicalSpotPriceLoading
    )
  }, [
    creatingReviewOrderForBuy,
    gettingLatestSpotPrice,
    historicalSpotPriceLoading
  ])

  const askPricePerMg = useMemo(() => {
    const { askPricePerMg } = latestSpotPriceData ?? {}
    return askPricePerMg ? Number(askPricePerMg) : 0
  }, [latestSpotPriceData])

  const updatedTime = useMemo(() => {
    const { spotTime } = latestSpotPriceData ?? {}
    // Raw string, not new Date(): the API emits offsetless UTC, which Date()
    // reads as NZ local and renders the UTC wall clock [BUG-032].
    return formatDateToString(spotTime ?? '')
  }, [latestSpotPriceData])

  // Andrew's comment: In here, where we have latestSpotPriceData, we _should_ be able to replace the last element of the `builtData` with the data from latestSpotPrice.
  // because historicalSpotPriceData only includes an older spotPrice generated once per day.
  const builtData: DataPoint[] = useMemo(
    () =>
      historicalSpotPriceData && historicalSpotPriceData.length > 0
        ? buildDataHistoricalSpotPrices(historicalSpotPriceData, askPricePerMg)
        : [],
    [historicalSpotPriceData, askPricePerMg]
  )

  // Returns the quantity (in the product's unit_type) purchasable for a
  // given dollar amount. Naming preserved for diff minimisation; the
  // function no longer has anything to do with decigrams.
  //
  // Returns 0 — NOT MIN_QUANTITY — for a blank or zero amount, so the box can be
  // EMPTY while the customer is clearing it. Flooring to MIN_QUANTITY here priced
  // an empty field back to "$0.24" and wrote that into the input, so the field
  // could never be emptied and the next digit typed merged with the leftover
  // decimals. The blur handler still clamps to MIN_QUANTITY, so a finished entry
  // is always tradable.
  const calculateDecigrams = useCallback(
    (amount: number) =>
      amount > 0 && askPricePerMg > 0
        ? Math.round(amount / Number(askPricePerMg))
        : 0,
    [askPricePerMg]
  )

  const handleChangeWeightInput = (e: ChangeEvent<HTMLInputElement>) => {
    const weightGrams = Number(e.target.value)
    // Rounded, because grams * 1000 is not exact in binary: the box renders
    // weightSlider/1000 to 3dp, and 1.001 * 1000 === 1000.9999999999999. An equality
    // guard therefore failed for ~2% of milligram values, and each miss called
    // setWeight — which rewrites the AMOUNT box out from under whatever the customer
    // was typing. InputNumber no longer forwards programmatic changes at all, so this
    // can no longer be reached that way; it is correct now rather than load-bearing.
    const weightMg = Math.round(weightGrams * 1000)

    if (weightMg === weightSlider) {
      return
    }
    setEntryMode('quantity')
    setWeight(weightMg, askPricePerMg)
  }

  // Keep EXACTLY what was typed in the box, and derive the quantity from it.
  //
  // This used to convert the typed dollars to milligrams, round, convert back to
  // dollars and write that result into the input — replacing the customer's text on
  // every keystroke. Combined with a field that could not be emptied, a digit typed
  // into a leftover "$0.24" became "$20.34", the next "$250.28", the next
  // "$2,500.21": the number leapt on every press and an entered amount landed ~10x
  // out. Worst on mobile, where tapping puts the caret mid-number and there is no
  // easy select-all. Normalising belongs on blur, once they have finished typing.
  const handleChangeAmountInput = (e: ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value
    if (typed === amount) {
      return
    }
    setAmount(typed)
    setEntryMode('amount')
    setWeightSlider(
      Math.min(calculateDecigrams(Number(typed)), MAX_QUANTITY_MG)
    )
  }

  // On the way out, keep the customer's figure and only bring the QUANTITY into the
  // tradable range. The amount itself is no longer rewritten.
  //
  // It used to snap to the nearest whole milligram and price that back, so finishing
  // an entry changed the number: type $88.55, tap away, watch it become $88.51. Gold
  // is sold in whole milligrams (~24c each) so an arbitrary dollar figure can never
  // land exactly on one — quantity-first ordering made that the customer's problem.
  // The order is now placed amount-first, where the backend solves the after-fee
  // weight around the entered total, so there is nothing left to round.
  const handleOnBlurAmountInput = () => {
    const typed = Number(amount)
    if (!(typed > 0)) {
      // An empty or zero box still has to become something orderable.
      setWeight(MIN_QUANTITY, askPricePerMg)
      setEntryMode('quantity')
      return
    }
    setWeightSlider(
      Math.max(
        Math.min(calculateDecigrams(typed), MAX_QUANTITY_MG),
        MIN_QUANTITY
      )
    )
  }

  const handleIncreaseWeight = useCallback(() => {
    if (!loading) {
      const next = incrementQuantity(weightSlider, MAX_QUANTITY_MG)
      setWeight(next, askPricePerMg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askPricePerMg, amount, loading, weightSlider])

  const handleDecreaseWeight = useCallback(() => {
    if (!loading) {
      const next = decrementQuantity(weightSlider, MIN_QUANTITY)
      setWeight(next, askPricePerMg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askPricePerMg, amount, loading, weightSlider])

  // Held platinum with the flag off can be sold but not bought. Same sell flow
  // as the My Vault trade drawer: a sell review order, then the vault's sell
  // screen, which lets the customer pick the weight.
  const handleClickSell = useCallback(async () => {
    try {
      const reviewOrder = await createReviewOrderMutation.mutateAsync({
        productId,
        orderType: 'sell',
        quantityMg: 100
      })
      if (reviewOrder?.id) {
        navigate(
          myVaultPath({
            queryParams: { type: 'sell', id: reviewOrder.id, productId }
          })
        )
      }
    } catch (err) {
      const error = err as { message?: string; errorStatus?: string }
      if (error?.errorStatus === TRADING_IS_PAUSED) {
        setTradingPaused(true)
      }
      setToast({
        open: true,
        message: error?.message ?? 'An error occurred',
        type: 'error',
        duration: 3000
      })
    }
  }, [createReviewOrderMutation, navigate, productId, setToast])

  const goToReferralPage = useCallback(() => {
    navigate(referralsPath())
  }, [navigate])

  useEffect(() => {
    const header = document.getElementById('app-header')
    const productBullion = document.getElementById('button-wrapper')
    if (header) {
      setHeaderHeight(header.offsetHeight)
    }
    if (productBullion) {
      setProductBullionHeight(productBullion.offsetHeight)
    }

    window?.addEventListener('scroll', () => {
      const header = document.getElementById('app-header')
      const productBullion = document.getElementById('button-wrapper')
      const slideWrapper = document.getElementById('slide-wrapper')
      if (slideWrapper && header && productBullion) {
        if (
          slideWrapper.getBoundingClientRect().top >=
          Number(header.offsetHeight) + Number(productBullion.offsetHeight)
        ) {
          setHeaderHeight(header.offsetHeight)
          header.style.top = '0'
        } else {
          setHeaderHeight(0)
          header.style.top = `-${header.offsetHeight}px`
        }
      }
    })
  }, [])

  useEffect(() => {
    if (weightSlider && !Number.isInteger(weightSlider)) {
      setError({
        message: 'Enter a value with up to 3 decimal places',
        error: true
      })
    } else if (weightSlider > MAX_QUANTITY_MG) {
      setError({
        message: 'You must provide a quantity between 0.001g and 500g',
        error: true
      })
    } else {
      setError(undefined)
    }
    setStep(stepFor(weightSlider))
  }, [weightSlider, unitType])

  const sliderScalingFactor = useMemo(() => {
    if (product?.slidingScalingFactor) {
      return product.slidingScalingFactor
    }

    return 1
  }, [product])

  if (tradingPaused) {
    return <PauseTrading />
  }

  return (
    <DefaultLayout
      wrapperContent={{ mt: 0, mb: 2, px: 0 }}
      wrapperContainer={{ p: 0, position: 'relative' }}
      showKiaOra
      showFooter
      pageTitle='Goldie'
    >
      <Box
        className={classes.buyButtonWrapper}
        component='section'
        id='button-wrapper'
        top={isDesktopView ? 0 : headerHeight}
      >
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          paddingX={2}
        >
          <Box sx={{ height: '40px' }}>
            <Typography
              variant='body1'
              textTransform='uppercase'
              lineHeight={1}
            >
              {product?.name} BULLION
            </Typography>
            <Box sx={{ display: 'flex', gap: '0.5rem', lineHeight: 1 }}>
              <Typography>LIVE: </Typography>
              <CountUpPrice
                value={Number(askPricePerMg ?? 0) * 1000}
                suffix='/g'
                prefix='$'
                className={classes.livePrice}
              />
            </Box>
          </Box>

          <Box>
            {product && !product.buyable ? (
              <Button
                onClick={handleClickSell}
                disabled={creatingReviewOrderForBuy}
                sx={{ borderRadius: '2rem', height: '38px', fontSize: '1rem' }}
              >
                Sell
              </Button>
            ) : product?.tradable ? (
              <Button
                onClick={onSubmit}
                sx={{ borderRadius: '2rem', height: '38px', fontSize: '1rem' }}
              >
                Buy: ${formattedPrice(amount)}
              </Button>
            ) : (
              <Button
                disabled={true}
                sx={{ borderRadius: '2rem', height: '38px', fontSize: '1rem' }}
              >
                Coming Soon!
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      <Box height='100%' position='relative'>
        <Box
          position='sticky'
          top={
            isDesktopView
              ? productBullionHeight
              : productBullionHeight + headerHeight
          }
          left={0}
          right={0}
          bottom={0}
          overflow='hidden'
          // height={`calc(100vh - ${
          //   isDesktopView ? 0 : headerHeight
          // }px - ${productBullionHeight}px)`}
          height={`calc(100vh - ${isDesktopView ? 0 : headerHeight}px - ${
            productBullionHeight + (isDesktopView ? 60 : 60)
          }px)`}
          minHeight={280}
          display='flex'
          flexDirection='column'
          pt={0.25}
          sx={{
            transition: 'height 0.5s'
          }}
          gap={2}
        >
          <Box display='flex' flexDirection='column'>
            <Stack
              position='relative'
              justifyContent='center'
              alignItems='flex-end'
              //marginLeft={2}
              px={2}
              height={50}
              minHeight={50}
            >
              <Box height={1}>
                <Typography variant='body2' textAlign='right' color='error'>
                  {error?.message}
                </Typography>
              </Box>
              <InputNumber
                name='amount'
                value={amount}
                onChange={handleChangeAmountInput}
                onBlur={handleOnBlurAmountInput}
                // Select the whole amount on focus so typing REPLACES it.
                // react-number-format parks the caret just after the "Edit: $"
                // prefix on tap, so without this every typed digit is inserted in
                // FRONT of the existing figure — tapping and typing 250 against a
                // default of $100.11 gave "$250,100.11". On a phone there is no
                // easy select-all to work around it.
                //
                // Deferred: the library repositions the caret after its own focus
                // handler runs, which undoes a synchronous select().
                onFocus={e => {
                  const el = e.target
                  setTimeout(() => el.select(), 0)
                }}
                unitType={unitType}
              />
              <InputNumber
                name='weight'
                value={weightSlider / 1000}
                onChange={handleChangeWeightInput}
                unitType={unitType}
              />
            </Stack>
          </Box>

          <Box
            display='flex'
            flexDirection='row'
            alignItems='flex-start'
            justifyContent='space-between'
            maxHeight='calc(100% - 70px - 110px)' // magical px values correspond to boxes above and below
            position='relative'
          >
            {product && (
              <img
                src={product?.image}
                alt={`${product.name}-bullion`}
                className={classes.productImage}
              />
            )}
            <Box
              display='flex'
              flexDirection='column'
              justifyContent='space-between'
              alignItems='center'
              position='absolute'
              top='0'
              right='0'
              bottom='0'
              mr={2}
              gap={2}
            >
              <Box onClick={handleIncreaseWeight} textAlign='center'>
                <KeyboardArrowUpIcon fontSize='large' color='primary' />
              </Box>
              <SliderMui
                aria-label='weight'
                id='weight'
                size='small'
                orientation='vertical'
                valueLabelDisplay='off'
                defaultValue={0}
                step={step}
                min={MIN_QUANTITY}
                max={Math.min(50_000 * sliderScalingFactor, MAX_QUANTITY_MG)}
                sx={{ height: '100%' }}
                name='weight'
                value={weightSlider}
                onChange={(_event, value) => {
                  setEntryMode('quantity')
                  setWeight(Number(value) || MIN_QUANTITY, askPricePerMg)
                }}
                className={classes.slider}
                disabled={loading}
              />
              <Box onClick={handleDecreaseWeight} textAlign='center'>
                <KeyboardArrowDownIcon fontSize='large' color='primary' />
              </Box>
            </Box>
          </Box>
          <Box
            display='flex'
            minHeight='95px'
            justifyContent='center'
            gap={1}
            alignItems='center'
            px={2}
            overflow='auto'
            mb={1}
          >
            {products &&
              products.map(p => {
                return (
                  <Box
                    key={p.id}
                    display='flex'
                    flexDirection='column'
                    justifyContent='center'
                    alignItems='center'
                    gap={1}
                    onClick={() => {
                      navigate(tradePath({ routeParams: { productId: p.id } }))
                    }}
                  >
                    <img
                      src={p?.image}
                      alt={`${p.name}-bullion`}
                      className={classes.productSwitcherImage}
                    />
                    <Box
                      className={
                        Number(p?.id) === productId
                          ? classes.activeDot
                          : classes.dot
                      }
                    />
                  </Box>
                )
              })}
          </Box>
          <Box display='flex' minHeight='0' flex='1'>
            {/*filler box -- fills up remaining free space*/}
          </Box>
        </Box>
        <Box className={classes.stickyLine} mt={1}></Box>
        <Box
          sx={{
            background: 'transparent',
            backdropFilter: 'blur(20px)'
            // borderTop: ({ palette }) => `1px solid ${palette.black.main}`
          }}
          pt={1}
          id='slide-wrapper'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            px={2}
          >
            <Box>
              <Button
                variant='outlined'
                sx={{
                  borderRadius: '2rem',
                  padding: '0 0.5rem',
                  height: '38px'
                }}
                onClick={goToReferralPage}
              >
                <Typography variant='body1' color='primary'>
                  SHARE $20
                </Typography>
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Typography>CREDIT: </Typography>
              <CountUpPrice value={Number(user?.wallet?.balance)} prefix='$' />
            </Box>
          </Box>
          <Divider className={classes.divider} />
          <Stack height={300} mb={10} position='relative'>
            <Typography
              className={classes.aboutDesc}
              paddingLeft={2}
              textTransform='uppercase'
            >
              {product?.name} PERFORMANCE
            </Typography>
            <LoadingWrapper loading={historicalSpotPriceLoading}>
              <LineGraph
                data={builtData}
                graphPeriod={graphPeriod}
                beginAtZero={false}
                colors={{
                  line: theme.palette.black.main,
                  tooltipCircle: theme.palette.primary.main,
                  axis: theme.palette.black.main
                }}
                tickFormatter={(value: NumberValue) =>
                  `$${Number(value).toFixed(2)}`
                }
                showChangeValue={false}
                showXAxis
                loading={historicalSpotPriceLoading}
                showBackgroundColor={false}
                isCustomTooltip={false}
              />
            </LoadingWrapper>
          </Stack>
          <Stack>
            <GraphPeriod
              setGraphPeriod={setGraphPeriod}
              graphPeriod={graphPeriod}
            />
          </Stack>
          <Stack>
            <Typography
              textAlign='right'
              fontSize='0.75rem'
              textTransform='uppercase'
              px={1}
            >
              UPDATED {updatedTime.formattedHours}:
              {updatedTime.formattedMinutes} {updatedTime.meridiem}{' '}
              {updatedTime.formattedDate}
            </Typography>
          </Stack>
          <Stack
            px={2}
            my={2}
            borderTop={({ palette }) => `1px solid ${palette.black.main}`}
          >
            <Typography className={classes.aboutDesc} color='primary'>
              ABOUT
            </Typography>
            {product?.descriptions.map((description, index) => (
              <Typography
                className={classes.aboutDescSecond}
                key={index}
                fontSize={index === 0 ? 'x-large' : undefined}
              >
                {description}
              </Typography>
            ))}
          </Stack>

          <Stack
            px={2}
            borderTop={({ palette }) => `1px solid ${palette.black.main}`}
          >
            <Typography className={classes.aboutDesc}>LIVE LOCATION</Typography>
            <Location />
          </Stack>
        </Box>
      </Box>
    </DefaultLayout>
  )
}
